<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { Line } from 'vue-chartjs'
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  type TooltipItem,
} from 'chart.js'
import Decimal from 'decimal.js'
import { InstantRangeInput } from '@hantera/design-system'
import { type JobStatisticsData, useEvents, useLocalization } from '@hantera/portal-app'
import useBackend from '@/composition/useBackend'
import type { JobStatisticBucket } from '@/backend/jobs.types'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

const props = defineProps<{
  jobDefinitionId: string
}>()

const t = useLocalization('jobs')
const { jobs: jobsService } = useBackend()
const events = useEvents()

// State
const loading = ref(false)
const statistics = ref<JobStatisticBucket[]>([])
const dateRange = ref<{ from: Date; to: Date }>({
  from: new Date(Date.now() - 24 * 60 * 60 * 1000),
  to: new Date(),
})

/**
 * Calculates optimal bucket size in minutes based on the date range span.
 */
function calculateBucketMinutes(rangeMs: number): number {
  const hours = rangeMs / (1000 * 60 * 60)

  if (hours <= 2) return 1
  if (hours <= 6) return 5
  if (hours <= 24) return 15
  if (hours <= 72) return 60
  if (hours <= 168) return 360
  return 1440
}

/**
 * Rounds a date down to the start of a bucket boundary
 */
function roundToBucket(date: Date, bucketMinutes: number): Date {
  const result = new Date(date)
  result.setSeconds(0, 0)

  if (bucketMinutes >= 1440) {
    result.setHours(0, 0, 0, 0)
  } else if (bucketMinutes >= 60) {
    const hours = Math.floor(bucketMinutes / 60)
    result.setMinutes(0)
    result.setHours(Math.floor(result.getHours() / hours) * hours)
  } else {
    result.setMinutes(Math.floor(result.getMinutes() / bucketMinutes) * bucketMinutes)
  }

  return result
}

// Computed values
const bucketMinutes = computed(() => {
  const rangeMs = dateRange.value.to.getTime() - dateRange.value.from.getTime()
  return calculateBucketMinutes(rangeMs)
})

const sortedBucketTimes = computed(() => {
  const times: string[] = []
  const minutes = bucketMinutes.value

  const start = roundToBucket(dateRange.value.from, minutes)
  const end = dateRange.value.to

  const current = new Date(start)
  while (current <= end) {
    times.push(current.toISOString())
    current.setMinutes(current.getMinutes() + minutes)
  }

  return times
})

const timeLabels = computed(() => {
  const minutes = bucketMinutes.value

  return sortedBucketTimes.value.map((iso) => {
    const date = new Date(iso)

    if (minutes >= 1440) {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
    } else {
      const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric' })
      const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      return `${dateStr} ${timeStr}`
    }
  })
})

/**
 * Maps raw statistics to display buckets
 */
const bucketMapping = computed(() => {
  const minutes = bucketMinutes.value
  const mapping = new Map<string, string[]>()

  for (const stat of statistics.value) {
    const rawTime = new Date(stat.bucketTime)
    const displayBucket = roundToBucket(rawTime, minutes).toISOString()
    const rawBucket = rawTime.toISOString()

    if (!mapping.has(displayBucket)) {
      mapping.set(displayBucket, [])
    }
    const existing = mapping.get(displayBucket)!
    if (!existing.includes(rawBucket)) {
      existing.push(rawBucket)
    }
  }

  return mapping
})

const rawStatsByBucket = computed(() => {
  const map = new Map<string, JobStatisticBucket>()
  for (const stat of statistics.value) {
    const bucketKey = new Date(stat.bucketTime).toISOString()
    map.set(bucketKey, stat)
  }
  return map
})

/**
 * Aggregate statistics into display buckets
 */
const aggregatedData = computed(() => {
  const successful: number[] = []
  const failed: number[] = []
  const queueDepth: number[] = []

  for (const displayBucketKey of sortedBucketTimes.value) {
    const rawBuckets = bucketMapping.value.get(displayBucketKey) || []

    let totalSuccessful = new Decimal(0)
    let totalFailed = new Decimal(0)
    let maxQueueDepth = new Decimal(0)

    for (const rawKey of rawBuckets) {
      const stat = rawStatsByBucket.value.get(rawKey)
      if (stat) {
        totalSuccessful = totalSuccessful.add(stat.successful)
        totalFailed = totalFailed.add(stat.failed)
        if (stat.totalQueueDepth !== null && stat.totalQueueDepth.gt(maxQueueDepth)) {
          maxQueueDepth = stat.totalQueueDepth
        }
      }
    }

    successful.push(totalSuccessful.toNumber())
    failed.push(totalFailed.toNumber())
    queueDepth.push(maxQueueDepth.toNumber())
  }

  return { successful, failed, queueDepth }
})

/**
 * Aggregate execution time statistics into display buckets
 */
const executionTimeData = computed(() => {
  const minTime: (number | null)[] = []
  const avgTime: (number | null)[] = []
  const maxTime: (number | null)[] = []

  for (const displayBucketKey of sortedBucketTimes.value) {
    const rawBuckets = bucketMapping.value.get(displayBucketKey) || []

    let bucketMin: Decimal | null = null
    let bucketMax: Decimal | null = null
    let totalAvg = new Decimal(0)
    let avgCount = 0

    for (const rawKey of rawBuckets) {
      const stat = rawStatsByBucket.value.get(rawKey)
      if (stat) {
        if (stat.minExecution !== null && stat.minExecution !== undefined) {
          if (bucketMin === null || stat.minExecution.lt(bucketMin)) {
            bucketMin = stat.minExecution
          }
        }
        if (stat.maxExecution !== null && stat.maxExecution !== undefined) {
          if (bucketMax === null || stat.maxExecution.gt(bucketMax)) {
            bucketMax = stat.maxExecution
          }
        }
        if (stat.avgExecution !== null && stat.avgExecution !== undefined) {
          totalAvg = totalAvg.add(stat.avgExecution)
          avgCount++
        }
      }
    }

    minTime.push(bucketMin?.toNumber() ?? null)
    maxTime.push(bucketMax?.toNumber() ?? null)
    avgTime.push(avgCount > 0 ? totalAvg.div(avgCount).toNumber() : null)
  }

  return { minTime, avgTime, maxTime }
})

const chartData = computed(() => ({
  labels: timeLabels.value,
  datasets: [
    {
      label: t('detail.statistics.successful'),
      data: aggregatedData.value.successful,
      borderColor: 'hsl(142, 70%, 45%)',
      backgroundColor: 'hsla(142, 70%, 45%, 0.3)',
      fill: true,
      tension: 0.3,
      order: 2,
    },
    {
      label: t('detail.statistics.failed'),
      data: aggregatedData.value.failed,
      borderColor: 'hsl(0, 70%, 50%)',
      backgroundColor: 'hsla(0, 70%, 50%, 0.3)',
      fill: true,
      tension: 0.3,
      order: 1,
    },
    {
      label: t('detail.statistics.queueDepth'),
      data: aggregatedData.value.queueDepth,
      borderColor: 'hsl(200, 70%, 50%)',
      backgroundColor: 'hsla(200, 70%, 50%, 0.3)',
      fill: true,
      tension: 0.3,
      order: 3,
    },
  ],
}))

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index' as const,
    intersect: false,
  },
  plugins: {
    legend: {
      position: 'top' as const,
    },
    tooltip: {
      mode: 'index' as const,
      intersect: false,
    },
  },
  scales: {
    x: {
      display: true,
      title: {
        display: false,
      },
    },
    y: {
      type: 'linear' as const,
      display: true,
      position: 'left' as const,
      title: {
        display: true,
        text: t('detail.statistics.jobs'),
      },
      stacked: true,
      beginAtZero: true,
    },
  },
}))

const executionTimeChartData = computed(() => ({
  labels: timeLabels.value,
  datasets: [
    {
      label: t('detail.statistics.minTime'),
      data: executionTimeData.value.minTime,
      borderColor: 'hsl(200, 70%, 60%)',
      backgroundColor: 'hsla(200, 70%, 50%, 0.2)',
      fill: false,
      tension: 0.3,
      pointRadius: 2,
      order: 3,
    },
    {
      label: t('detail.statistics.avgTime'),
      data: executionTimeData.value.avgTime,
      borderColor: 'hsl(200, 70%, 45%)',
      backgroundColor: 'transparent',
      fill: false,
      tension: 0.3,
      borderWidth: 2,
      pointRadius: 3,
      order: 2,
    },
    {
      label: t('detail.statistics.maxTime'),
      data: executionTimeData.value.maxTime,
      borderColor: 'hsl(200, 70%, 30%)',
      backgroundColor: 'hsla(200, 70%, 50%, 0.2)',
      fill: '-2',
      tension: 0.3,
      pointRadius: 2,
      order: 1,
    },
  ],
}))

const executionTimeChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index' as const,
    intersect: false,
  },
  plugins: {
    legend: {
      position: 'top' as const,
    },
    tooltip: {
      mode: 'index' as const,
      intersect: false,
      callbacks: {
        label: (context: TooltipItem<'line'>) => {
          const label = context.dataset.label || ''
          const value = context.parsed.y
          if (value === null) return `${label}: N/A`
          return `${label}: ${value.toFixed(0)} ms`
        },
      },
    },
  },
  scales: {
    x: {
      display: true,
      title: {
        display: false,
      },
    },
    y: {
      type: 'linear' as const,
      display: true,
      position: 'left' as const,
      title: {
        display: true,
        text: t('detail.statistics.timeMs'),
      },
      beginAtZero: true,
    },
  },
}))

const hasExecutionTimeData = computed(() => {
  return executionTimeData.value.minTime.some((v) => v !== null)
})

async function loadStatistics() {
  loading.value = true
  try {
    const result = await jobsService.getStatistics({
      jobDefinitionId: props.jobDefinitionId,
      from: dateRange.value.from,
      to: dateRange.value.to,
    })
    const definition = result.definitions.find((d) => d.jobDefinitionId === props.jobDefinitionId)
    statistics.value = definition?.buckets ?? []
  } catch (e) {
    console.error('Failed to load statistics:', e)
  } finally {
    loading.value = false
  }
}

function handleDateRangeChange(value: { from: Date; to: Date } | undefined) {
  if (value) {
    dateRange.value = value
    loadStatistics()
  }
}

function handleStatisticsEvent(data: JobStatisticsData) {
  const bucketTime = new Date(data.bucketTimestamp)

  if (bucketTime < dateRange.value.from || bucketTime > dateRange.value.to) {
    return
  }

  const bucketKey = bucketTime.toISOString()

  // Find existing bucket and update (deduplication by bucket timestamp)
  const existingIndex = statistics.value.findIndex(
    (s) => new Date(s.bucketTime).toISOString() === bucketKey
  )

  const newStatistic: JobStatisticBucket = {
    bucketTime: bucketTime,
    scheduled: new Decimal(data.scheduled),
    successful: new Decimal(data.successful),
    failed: new Decimal(data.failed),
    minExecution: data.minExecutionMs !== null ? new Decimal(data.minExecutionMs) : null,
    maxExecution: data.maxExecutionMs !== null ? new Decimal(data.maxExecutionMs) : null,
    avgExecution: data.avgExecutionMs !== null ? new Decimal(data.avgExecutionMs) : null,
    p95Execution: data.p95ExecutionMs !== null ? new Decimal(data.p95ExecutionMs) : null,
    cacheQueueDepth: null,
    totalQueueDepth: null,
  }

  if (existingIndex >= 0) {
    statistics.value[existingIndex] = newStatistic
  } else {
    statistics.value.push(newStatistic)
    statistics.value.sort((a, b) => new Date(a.bucketTime).getTime() - new Date(b.bucketTime).getTime())
  }
}

let unsubscribe: (() => void) | null = null

function setupEventSubscription() {
  // Clean up previous subscription
  if (unsubscribe) {
    unsubscribe()
    unsubscribe = null
  }

  if (events.available) {
    unsubscribe = events.subscribe({
      path: `job-definitions/${props.jobDefinitionId}`,
      events: ['jobStatistics'],
      onEvent: (event) => {
        if (event.eventType === 'jobStatistics') {
          handleStatisticsEvent(event.data)
        }
      },
    })
  }
}

onMounted(() => {
  loadStatistics()
  setupEventSubscription()
})

watch(
  () => props.jobDefinitionId,
  () => {
    loadStatistics()
    setupEventSubscription()
  }
)
</script>

<template>
  <div class="statistics-tab">
    <div class="date-range-picker">
      <label>{{ t('detail.statistics.dateRange') }}</label>
      <InstantRangeInput :model-value="dateRange" @update:model-value="handleDateRangeChange" />
    </div>

    <div v-if="loading" class="loading">
      {{ t('detail.statistics.loading') }}
    </div>

    <template v-else>
      <div class="chart-section">
        <h3>{{ t('detail.statistics.jobCountTitle') }}</h3>
        <div class="chart-container">
          <Line :data="chartData" :options="chartOptions" />
        </div>
      </div>

      <div v-if="hasExecutionTimeData" class="chart-section">
        <h3>{{ t('detail.statistics.executionTimeTitle') }}</h3>
        <div class="chart-container">
          <Line :data="executionTimeChartData" :options="executionTimeChartOptions" />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.statistics-tab {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  gap: 16px;
}

.date-range-picker {
  display: flex;
  align-items: center;
  gap: 12px;
}

.date-range-picker label {
  font-weight: 500;
  white-space: nowrap;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: var(--color-text-secondary);
}

.chart-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chart-section h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.chart-container {
  flex: 1;
  min-height: 300px;
}
</style>
